import React from 'react';
import { Helmet } from 'react-helmet-async';
import './Methodology.css';
import SearchScreenNav from '../components/SearchScreenNav';

const Methodology = () => {
  return (
    <div className="methodology-page-container">
      <Helmet>
        <title>Methodology | LGBT PridePath</title>
        <meta name="description" content="Learn about LGBT PridePath's research methodology. Our legal data comes from MAP (Movement Advancement Project) and our resource database contains over 20,000 LGBTQ+ resources." />
        <link rel="canonical" href="https://www.lgbtpridepath.org/methodology" />
      </Helmet>
      
      {/* Custom navigation for overlay effect */}
      <SearchScreenNav />
      
      {/* Hero image container - positioned OUTSIDE content wrapper like About page */}
      <div className="methodology-hero-image-container">
        <img src={require('../assets/header-methodology.png')} alt="Methodology illustration" />
      </div>
      
      {/* Content wrapper matching About structure */}
      <div className="methodology-content-wrapper">
        {/* Hero section */}
        <div className="methodology-hero-section">
          <h2 className="methodology-hero-subtitle">METHODOLOGY</h2>
          <h1 className="methodology-hero-title">Research and Informational background</h1>
        </div>
      </div>

      {/* Information Section - appears on white background from pseudo-element */}
      <section className="methodology-info-section">
        <div className="methodology-info-content">
          <div className="methodology-info-image">
            <img src={require('../assets/methodolgylower.png')} alt="Information For You" />
            <div className="methodology-info-image-overlay">
              <h3>Information<br/>For You</h3>
            </div>
          </div>
          <div className="methodology-info-text">
            <div className="methodology-section-header"></div>
            
            {/* Important Upfront Statement */}
            <div className="methodology-important-notice">
              <h3>IMPORTANT NOTICE</h3>
              <p>
                In these very uncertain and changing times, it is important to understand that in some cases a law may protect someone, but not everyone may follow that law. So the information presented on this website is based on the best information we have on laws in place, but sadly, nobody can guarantee that a particular entity will choose to offer that protection.
              </p>
            </div>
            
            <div className="methodology-services-box">
              <h3>FREQUENTLY ASKED QUESTIONS</h3>
              <ul>
                <li>
                  <strong>Who runs this website?</strong><br/>
                  LGBT PridePath is a program of the <a href="https://lgbthotline.org" target="_blank" rel="noopener noreferrer">LGBT National Help Center</a>. We have been around for almost 30 years, and we offer many different programs for our community, including various hotlines and other online services. You can learn more about us at  <a href="https://lgbthotline.org/" target="_blank" rel="noopener noreferrer">LGBThotline.org</a>.
                </li>
                <li>
                  <strong>Is there a charge to use this, or does anyone sponsor you to give their results?</strong><br/>
                  Nope. Everything we do, both this program and all of our others, have always been completely free of charge. Donations are gladly welcome at  <a href="https://lgbthotline.org/donate" target="_blank" rel="noopener noreferrer">LGBThotline.org/donate</a>.
                </li>
                <li>
                  <strong>Where do you get your data regarding the laws in each location?</strong><br/>
                  We are very grateful for the support of <a href="https://lgbtmap.org" target="_blank" rel="noopener noreferrer">MAP, the Movement Advancement Project</a>, that supplies us with our legal data. Their website is  <a href="https://lgbtmap.org" target="_blank" rel="noopener noreferrer">LGBTmap.org</a>.
                </li>
                <li>
                  <strong>What if I think there is an error in the information about a particular law?</strong><br/>
                  Please contact MAP directly at <a href="mailto:info@mapresearch.org">info@mapresearch.org</a>.
                </li>
                <li>
                  <strong>Who do I contact if I have a question about the results I am receiving?</strong><br/>
                  You can contact us directly at <a href="mailto:LGBTPridePath@LGBThotline.org">LGBTPridePath@LGBThotline.org</a>.
                </li>
                <li>
                  <strong>Where do you get your information in the search results concerning local community centers or other local resources?</strong><br/>
                  We maintain the largest LGBTQ resource database of its kind in the world, with over 20,000 resources. You can access all of our resources directly at our website at <a href="https://lgbtnearme.org" target="_blank" rel="noopener noreferrer">LGBTnearMe.org</a>. If you feel there might be a need for an update, please email us at <a href="mailto:Help@LGBThotline.org">Help@LGBThotline.org</a>.
                </li>
                <li>
                  <strong>How do I find information about the meaning of some of the terms used in the laws?</strong><br/>
                  See below.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Source Material Section */}
      <section className="methodology-source-section">
        <div className="methodology-source-content">
          <div className="methodology-source-text">
            <div className="methodology-section-header"></div>
            <div className="methodology-history-box">
              <h3>Law Terms Used</h3>
              
              {/* Legal Terms List */}
              <div className="methodology-legal-terms-list">
                <div className="legal-term">
                  <h5>EMPLOYMENT NON-DISCRIMINATION</h5>
                  <p>Protects from being unfairly fired, not hired, or discriminated against in the workplace by private employers.</p>
                </div>
                
                <div className="legal-term">
                  <h5>HOUSING NON-DISCRIMINATION</h5>
                  <p>Protects from being unfairly evicted, denied housing or refused the ability to rent or buy housing.</p>
                </div>
                
                <div className="legal-term">
                  <h5>PUBLIC ACCOMMODATION NON-DISCRIMINATION</h5>
                  <p>Protects from being unfairly refused service from or entry to places accessible to the general public. Includes retail stores, restaurants, parks, hotels, doctor's office and banks, among others.</p>
                </div>
                
                <div className="legal-term">
                  <h5>NON-DISCRIMINATION FOR STATE EMPLOYEES</h5>
                  <p>Protects state employees from discrimination on the basis of sexual orientation and/or gender identity.</p>
                </div>
                
                <div className="legal-term">
                  <h5>SECOND PARENT ADOPTION FOR UNMARRIED COUPLES</h5>
                  <p>Permits a non-biological parent in a relationship to adopt their partner's child and become a legal parent of that child, giving the child two legal parents and giving both parents legal rights.</p>
                </div>
                
                <div className="legal-term">
                  <h5>ADOPTION NON-DISCRIMINATION PROTECTIONS</h5>
                  <p>Protects from discrimination by adoption agencies and officials.</p>
                </div>
                
                <div className="legal-term">
                  <h5>FOSTER-CARE NON-DISCRIMINATION PROTECTIONS</h5>
                  <p>Protects foster parents from discrimination by foster care agencies and officials.</p>
                </div>
                
                <div className="legal-term">
                  <h5>RECOGNITION FOR PARENTS USING ASSISTED REPRODUCTION</h5>
                  <p>Protects when a child is conceived through donor insemination or others forms of assisted reproduction. Grants legal parenting rights to the non-gestational, non-genetic parent. Some states require marriage of both partners, others do not.</p>
                </div>
                
                <div className="legal-term">
                  <h5>INCLUSIVE DEFINITIONS OF SPOUSE/PARTNER FOR FAMILY MEDICAL LEAVE</h5>
                  <p>Allows a person to take leave from work to care for their family members, including a spouse or partner. Some states require marriage of both partners, others do not.</p>
                </div>
                
                <div className="legal-term">
                  <h5>INCLUSIVE DEFINITIONS OF CHILDREN/PARENTS FOR FAMILY MEDICAL LEAVE</h5>
                  <p>Allows a person to take leave from work to care for their child. Some states expand definition beyond just biological or legal relationships.</p>
                </div>
                
                <div className="legal-term">
                  <h5>PRIVATE HEALTH INSURANCE NON-DISCRIMINATION LAWS</h5>
                  <p>Protects from being unfairly denied health insurance coverage or from being unfairly excluded from coverage for certain health care procedures.</p>
                </div>
                
                <div className="legal-term">
                  <h5>HEALTH INSURANCE PROVIDERS BANNED FROM EXCLUDING COVERAGE FROM TRANSGENDER-SPECIFIC CARE</h5>
                  <p>Bans health insurance issuers from denying or limiting coverage based on gender identity and require the removal of "transgender exclusion" from health plans.</p>
                </div>
                
                <div className="legal-term">
                  <h5>STATE ALLOWS TRANSGENDER-SPECIFIC CARE FOR YOUTH</h5>
                  <p>No laws banning best-practice medical care for transgender youth.</p>
                </div>
                
                <div className="legal-term">
                  <h5>NON-DISCRIMINATION LAWS AND POLICIES COVERING STUDENTS</h5>
                  <p>Protects students from discrimination in school, including being unfairly denied access to facilities, sports teams or clubs.</p>
                </div>
                
                <div className="legal-term">
                  <h5>ANTI-BULLYING LAWS AND POLICIES COVERING STUDENTS</h5>
                  <p>Protects students from bullying by other students, teachers and school staff.</p>
                </div>
                
                <div className="legal-term">
                  <h5>CONVERSION THERAPY BAN COVERING YOUTH</h5>
                  <p>Bans licensed mental health practitioners from subjecting minors to "conversion therapy" that attempts to change their sexual orientation and/or gender identity.</p>
                </div>
                
                <div className="legal-term">
                  <h5>STATE CURRICULAR STANDARDS REQUIRED TO BE INCLUSIVE</h5>
                  <p>Explicitly require the state's curricular standards to include LGBTQ people and history, such as subjects like history, civics or social studies.</p>
                </div>
                
                <div className="legal-term">
                  <h5>BAN ON "PANIC DEFENSE"</h5>
                  <p>Prohibits defendants to claim a victim's sexual orientation and/or gender identity provoked or caused the defendants actions.</p>
                </div>
              </div>
            </div>
          </div>
          <div className="methodology-source-image">
            <img src={require('../assets/mthodolgy lower bottom.png')} alt="Source Material" />
            <div className="methodology-source-image-overlay">
              <h3>Source<br/>Material</h3>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Methodology;